import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, GraduationCap, FileEdit, ArrowUpRight, Sparkles } from "lucide-react";
import { api, fmtDate } from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import { Card, PageHeader, Badge, Spinner } from "@/admin/ui";

const STATUS_TONE = { new: "amber", contacted: "blue", closed: "green" };

export default function Overview() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <Spinner />;

  const tiles = [
    { to: "/admin/enquiries", icon: Inbox, label: "Enquiries", value: s.enquiries, sub: `${s.enquiries_new} new · ${s.enquiries_week} this week`, testid: "stat-enquiries" },
    { to: "/admin/waitlist", icon: GraduationCap, label: "Academy waitlist", value: s.waitlist, sub: `${s.waitlist_week} joined this week`, testid: "stat-waitlist" },
    { to: "/admin/content", icon: FileEdit, label: "Content edits live", value: s.content_overrides, sub: "sections overriding defaults", testid: "stat-content" },
  ];

  return (
    <div data-testid="admin-overview">
      <PageHeader eyebrow="Overview" title={`Good day, ${user.name.replace(/^Mr\.?\s*/i, "").split(" ")[0]}.`} sub="Here's what's happening across desantrix.com." />
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {tiles.map((t) => (
          <Link key={t.label} to={t.to} data-testid={t.testid} className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:border-amber-400 hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/10 border border-amber-400/30"><t.icon className="h-5 w-5 text-amber-500" /></span>
              <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-amber-500 transition-colors" />
            </div>
            <p className="text-4xl font-extrabold tracking-tight text-[#0B1528]">{t.value}</p>
            <p className="text-sm font-semibold text-slate-700 mt-1">{t.label}</p>
            <p className="text-xs text-slate-500 mt-0.5">{t.sub}</p>
          </Link>
        ))}
      </div>

      <Card data-testid="recent-enquiries">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base md:text-lg font-semibold text-[#0B1528]">Latest enquiries</h2>
          <Link to="/admin/enquiries" className="text-xs font-semibold text-amber-600 hover:underline">View all</Link>
        </div>
        {s.recent_enquiries.length === 0 ? (
          <p className="text-sm text-slate-500 flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /> No enquiries yet — they'll appear here the moment someone writes in.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {s.recent_enquiries.map((e) => (
              <li key={e.id} className="py-3 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#0B1528] truncate">{e.name} <span className="font-normal text-slate-400">· {e.email}</span></p>
                  <p className="text-xs text-slate-500 truncate">{e.package} — {e.message}</p>
                </div>
                <Badge tone={STATUS_TONE[e.status || "new"]}>{e.status || "new"}</Badge>
                <span className="text-xs text-slate-400">{fmtDate(e.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
